import { createTimesheetSchema, type CreateTimesheetInput } from '@hirebotiq-ops/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { StatusBadge } from '../components/StatusBadge.js';
import { TimesheetEntryEditor } from '../components/TimesheetEntryEditor.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Field } from '../components/ui/field.js';
import { Input } from '../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/worker/timesheets')({
  component: WorkerTimesheets,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['worker']);
  },
});

function WorkerTimesheets() {
  const utils = trpc.useUtils();
  const list = trpc.timesheets.listMine.useQuery();
  const myAssignments = trpc.assignments.listMineAsWorker.useQuery();
  const create = trpc.timesheets.create.useMutation({
    onSuccess: () => {
      toast.success('Stundenzettel angelegt (Entwurf).');
      utils.timesheets.listMine.invalidate();
      form.reset({ periodStart: '', periodEnd: '', notes: '' } as never);
    },
    onError: (e) => toast.error(e.message),
  });
  const submit = trpc.timesheets.submit.useMutation({
    onSuccess: () => {
      toast.success('Eingereicht.');
      utils.timesheets.listMine.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const respond = trpc.timesheets.respondToCorrection.useMutation({
    onSuccess: (_, vars) => {
      toast.success(
        vars.action === 'confirm'
          ? 'Korrektur bestätigt — der Zettel läuft weiter zur Freigabe.'
          : 'Widerspruch eingereicht — HireBotIQ klärt den Fall.',
      );
      utils.timesheets.listMine.invalidate();
      setDisputeFor(null);
    },
    onError: (e) => toast.error(e.message),
  });
  const resubmit = trpc.timesheets.resubmit.useMutation({
    onSuccess: () => {
      toast.success('Korrigiert neu eingereicht.');
      utils.timesheets.listMine.invalidate();
      setResubmitFor(null);
    },
    onError: (e) => toast.error(e.message),
  });
  const [disputeFor, setDisputeFor] = useState<string | null>(null);
  const [disputeNotes, setDisputeNotes] = useState('');
  const [resubmitFor, setResubmitFor] = useState<string | null>(null);
  const [resubmitHours, setResubmitHours] = useState('');
  const [activeEditorId, setActiveEditorId] = useState<string | null>(null);

  const form = useForm<CreateTimesheetInput>({ resolver: zodResolver(createTimesheetSchema) });
  const { register, handleSubmit, setValue, watch, formState } = form;

  return (
    <div className="min-h-screen p-6 max-w-3xl mx-auto space-y-6">
      <Link to="/worker" className="text-sm text-[var(--muted-foreground)] hover:text-foreground">
        ← Zurück zur Übersicht
      </Link>
      <h1 className="text-3xl font-bold">Meine Stunden</h1>

      <Card>
        <CardHeader>
          <CardTitle>Stundenzettel erfassen</CardTitle>
          <CardDescription>Zeitraum + Gesamtstunden, optional je Einsatz</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4 md:grid-cols-2"
            onSubmit={handleSubmit((v) => {
              const clean = Object.fromEntries(
                Object.entries(v).filter(([, val]) => val !== '' && val != null),
              ) as CreateTimesheetInput;
              create.mutate(clean);
            })}
          >
            <Field label="Einsatz (optional)" htmlFor="assignmentId">
              <Select
                value={watch('assignmentId') ?? undefined}
                onValueChange={(val) => setValue('assignmentId', val)}
              >
                <SelectTrigger id="assignmentId">
                  <SelectValue placeholder="Einsatz wählen" />
                </SelectTrigger>
                <SelectContent>
                  {myAssignments.data?.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.clientCompanyName ?? 'Einsatz'} ({a.startDate ?? '?'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Gesamtstunden" htmlFor="totalHours">
              <Input
                id="totalHours"
                type="number"
                step="0.5"
                min={0}
                {...register('totalHours', { valueAsNumber: true })}
              />
            </Field>
            <Field label="Von *" htmlFor="periodStart" error={formState.errors.periodStart?.message}>
              <Input id="periodStart" type="date" {...register('periodStart')} />
            </Field>
            <Field label="Bis *" htmlFor="periodEnd" error={formState.errors.periodEnd?.message}>
              <Input id="periodEnd" type="date" {...register('periodEnd')} />
            </Field>
            <Field label="Notizen" htmlFor="notes">
              <Input id="notes" {...register('notes')} />
            </Field>
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Speichere …' : 'Entwurf anlegen'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Meine Stundenzettel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {list.data?.length === 0 && (
            <p className="text-sm text-[var(--muted-foreground)]">Noch keine Stundenzettel.</p>
          )}
          {list.data?.map((t) => (
            <div
              key={t.id}
              className="space-y-3 rounded-md border border-[var(--border)] p-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  <span className="text-[var(--muted-foreground)]">
                    {t.periodStart} – {t.periodEnd}
                  </span>
                  {t.totalHours ? (
                    <span className="ml-2 font-medium">{t.totalHours} h</span>
                  ) : null}
                  <span className="ml-2">
                    <StatusBadge status={t.status} />
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setActiveEditorId((curr) => (curr === t.id ? null : t.id))
                    }
                  >
                    {activeEditorId === t.id ? 'Tageszeilen einklappen' : '📅 Tageszeilen erfassen'}
                  </Button>
                  {t.status === 'draft' && (
                    <Button
                      size="sm"
                      disabled={submit.isPending}
                      onClick={() => submit.mutate({ timesheetId: t.id })}
                    >
                      Einreichen
                    </Button>
                  )}
                </div>
              </div>

              {/* Interaktive Tageszeilerfassung & Live-Zuschlagsvorschau */}
              {activeEditorId === t.id && (
                <div className="pt-2">
                  <TimesheetEntryEditor
                    timesheetId={t.id}
                    periodStart={t.periodStart}
                    periodEnd={t.periodEnd}
                    timezone={t.timezone ?? 'Europe/Berlin'}
                    readOnly={t.status !== 'draft' && t.status !== 'rejected'}
                  />
                </div>
              )}

              {/* Kunden-Korrektur: Vorher/Nachher + Ein-Klick-Antwort */}
              {t.status === 'client_corrected' && (
                <div className="rounded-md bg-[var(--muted)]/40 p-3 space-y-2 text-sm">
                  <p>
                    Der Kunde hat deine Stunden korrigiert:{' '}
                    <s className="text-[var(--muted-foreground)]">{t.originalTotalHours} h</s>{' '}
                    <span className="font-medium">→ {t.correctedTotalHours} h</span>
                    {t.correctionNotes ? (
                      <span className="block text-xs text-[var(--muted-foreground)] mt-1">
                        Hinweis des Kunden: {t.correctionNotes}
                      </span>
                    ) : null}
                  </p>
                  {disputeFor === t.id ? (
                    <div className="space-y-2">
                      <Input
                        value={disputeNotes}
                        onChange={(e) => setDisputeNotes(e.target.value)}
                        placeholder="Warum stimmt die Korrektur nicht? (min. 10 Zeichen)"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setDisputeFor(null)}>
                          Abbrechen
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={disputeNotes.trim().length < 10 || respond.isPending}
                          onClick={() =>
                            respond.mutate({
                              timesheetId: t.id,
                              action: 'dispute',
                              notes: disputeNotes.trim(),
                            })
                          }
                        >
                          Widerspruch senden
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={respond.isPending}
                        onClick={() => respond.mutate({ timesheetId: t.id, action: 'confirm' })}
                      >
                        Korrektur bestätigen
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDisputeFor(t.id)}>
                        Widersprechen
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Ablehnung: Grund sichtbar + korrigiert neu einreichen */}
              {t.status === 'rejected' && (
                <div className="rounded-md bg-[oklch(0.95_0.05_25)] p-3 border border-[oklch(0.85_0.1_25)] space-y-3 text-sm text-[oklch(0.35_0.15_25)]">
                  <div className="space-y-1">
                    <p className="font-semibold flex items-center gap-1.5">
                      ❌ Stundenzettel abgelehnt
                      {t.rejectCategory ? ` (${t.rejectCategory})` : ''}
                    </p>
                    {(t.clientReviewNotes || t.reviewNotes) && (
                      <p className="text-xs text-[var(--foreground)] bg-[var(--card)] p-2 rounded border border-[var(--border)]">
                        <span className="font-medium">Begründung:</span>{' '}
                        {t.clientReviewNotes ?? t.reviewNotes}
                      </p>
                    )}
                  </div>
                  {resubmitFor === t.id ? (
                    <div className="space-y-2 bg-[var(--card)] p-3 rounded-md border border-[var(--border)] text-foreground">
                      <div className="font-medium text-xs">Angaben überarbeiten:</div>
                      <div className="grid gap-2 md:grid-cols-2">
                        <div>
                          <label className="text-xs text-[var(--muted-foreground)]">Stunden</label>
                          <Input
                            type="number"
                            step="0.25"
                            min={0}
                            value={resubmitHours}
                            onChange={(e) => setResubmitHours(e.target.value)}
                            placeholder="Stunden"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[var(--muted-foreground)]">Hinweis / Korrektur-Notiz</label>
                          <Input
                            value={disputeNotes}
                            onChange={(e) => setDisputeNotes(e.target.value)}
                            placeholder="Optionale Notiz"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <Button size="sm" variant="ghost" onClick={() => setResubmitFor(null)}>
                          Abbrechen
                        </Button>
                        <Button
                          size="sm"
                          disabled={resubmit.isPending}
                          onClick={() => {
                            const parsed = Number(resubmitHours.replace(',', '.'));
                            resubmit.mutate({
                              timesheetId: t.id,
                              ...(Number.isFinite(parsed) && resubmitHours !== ''
                                ? { totalHours: parsed }
                                : {}),
                              notes: disputeNotes ? disputeNotes.trim() : undefined,
                            });
                          }}
                        >
                          {resubmit.isPending ? 'Reiche ein …' : 'Korrigiert neu einreichen'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setResubmitFor(t.id);
                        setResubmitHours(t.totalHours ? String(t.totalHours) : '');
                        setDisputeNotes(t.notes ?? '');
                      }}
                    >
                      ✏️ Korrigieren &amp; neu einreichen
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
